import classNames from 'classnames';
import { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom';
import { USDollar } from './util.js';
import {
	Grant,
	Pagination,
	Facet,
	FilterList,
	TermSearch,
	RemoveFilterButton,
	Icon,
	TotalStatement,
	SortLink,
} from './components.js';

function parseNumericStr( str ) {
	return Number( str.replace( /[^\d-]/g, '' ) );
}

function parseUrlSearchParams( params = new URLSearchParams( location.search ) ) {
	const data = {};

	for ( let [ param, value ] of params.entries() ) {
		param = param.replace( /\[(?:\d+)?\]$/, '' );

		if ( data[ param ] ) {
			if ( ! Array.isArray( data[ param ] ) ) {
				data[ param ] = [ data[ param ] ];
			}

			data[ param ].push( value );
		} else {
			data[ param ] = value;
		}
	}

	return data;
}

function GrantsDb( {
	api = '/grant-api',
	attributes: {
		legend = 'Search the Database',
		searchPlaceholder = 'Enter search terms...',
		remark = '',
		asideLabel = 'Filter Results',
		searchLabel = 'Search',
		amountTh = 'Amount',
		resultsTh = 'Results',
		approvalDateTh = 'Approval Date',
		emptyMessage = 'No grants match your current filters',
		applyLabel = 'Apply',
		amountLabel = 'Amount',
		minAmountLabel = 'Minimum value',
		maxAmountLabel = 'Maximum value',
		minAmountPlaceholder = '$1',
		maxAmountPlaceholder = '$125,000,000',
		invalidAmountError = 'Please enter a valid range',
		errorReloadLabel = 'Click here to reload the page',
		grantLocationLabel = 'Benefiting Location',
		grantLocationSearchLabel = 'Search locations',
		granteeLabel = 'Grantees',
		granteeSearchLabel = 'Enter grantee name',
		clearLabel= 'Clear',
		programsLabel = 'Program',
		grantSubjectLabel = 'Subject',
		regionLabel = 'Region',
		grantTypeLabel = 'Grant Type',
		approvalDateLabel,
		minApprovalDateLabel,
		minApprovalDatePlaceholder,
		maxApprovalDateLabel,
		maxApprovalDatePlaceholder = new Date().getFullYear(),
		invalidDateError = 'Please enter a valid date range',
	},
	...props
} ) {
	const [ error, setError ]               = useState( null );
	const [ ready, setReady ]               = useState( false );
	const [ loading, setLoading ]           = useState( false );
	const [ canPushState, setCanPushState ] = useState( true );
	const [ isPopstate, setIsPopstate ]     = useState( false );
	const [ params, setParams ]             = useState( parseUrlSearchParams() );
	const [ response, setResponse ]         = useState( props.response || {} );
	const [ minAmountStr, setMinAmountStr ] = useState( params.min_amount ? USDollar.format( params.min_amount ) : '' );
	const [ maxAmountStr, setMaxAmountStr ] = useState( params.max_amount ? USDollar.format( params.max_amount ) : '' );
	const [ minYear, setMinYear ]           = useState( params.after || '' );
	const [ maxYear, setMaxYear ]           = useState( params.before || '' );
	const [ yearFilter, setYearFilter ]     = useState( [ params.after, params.before ].filter( ( val, i, arr ) => val && arr.indexOf( val ) === i ).join( ' - ' ) );
	const [ filterCount, setFilterCount ]   = useState( 0 );
	const [ filtersOpen, setFiltersOpen ]   = useState( false );
	const scrollRef                         = useRef();
	const searchRef                         = useRef();

	const {
		grants         = [],
		facets         = {},
		total_grants   = 0,
		total_grantees = 0,
		total_pages    = 0,
		total_amount   = 0,
		min_year       = 0,
		max_year       = 0,
		min_amount     = 0,
		max_amount     = 0,
		orderby        = 'date',
		order          = 'desc',
		search         = '',
		after          = '',
		before         = '',
	} = response;

	const minAmountInt       = parseNumericStr( minAmountStr );
	const maxAmountInt       = parseNumericStr( maxAmountStr );
	const minYearInt         = parseNumericStr( minYear );
	const maxYearInt         = parseNumericStr( maxYear );
	const amountRange        = [];
	let   isValidDateRange   = true;
	let   isValidAmountRange = true;

	if ( isNaN( minAmountInt ) || isNaN( maxAmountInt ) ) {
		isValidAmountRange = false;
	} else if ( ( minAmountStr && minAmountInt < 0 ) || ( maxAmountStr && maxAmountInt <= 0 ) || ( minAmountStr && maxAmountStr && maxAmountInt < minAmountInt ) ) {
		isValidAmountRange = false;
	}

	// allow 0, but otherwise default to 1
	if ( minAmountStr ) {
		if ( Number( params.min_amount ) === 0 ) {
			amountRange.push( 0 );
		} else {
			amountRange.push( params.min_amount > -1 ? Number( params.min_amount ) : 1 );
		}
	} else {
		amountRange.push( 1 );
	}

	if ( maxAmountStr && Number( params.max_amount ) > -1 ) {
		amountRange.push( Number( params.max_amount ) );
	} else {
		amountRange.push( max_amount ); // default to max amount from api
	}

	if ( minYear || maxYear ) {
		if ( isNaN( minYearInt ) || isNaN( maxYearInt ) ) {
			isValidDateRange = false;
		} else if ( minYearInt > 0 && maxYearInt > 0 && minYearInt > maxYearInt ) {
			isValidDateRange = false;
		} else if ( minYear && minYearInt < 2006 ) {
			// isValidDateRange = false;
		}
	}

	const filterLabelMap = {
		program_tax   : programsLabel,
		grant_subject : grantSubjectLabel,
		regions_tax   : regionLabel,
		grant_type    : grantTypeLabel,
		grantee       : granteeLabel,
		grant_location: grantLocationLabel,
	};

	function mergeParams(mergedParams) {

		// if no page in params, default to 1
		if (typeof mergedParams.page === 'undefined') {
			mergedParams.page = 1;
		}

		setParams( { ...params, ...mergedParams } );
	}

	function getUrlSearchParams() {
		const urlSearchParams = new URLSearchParams();

		for ( let param of Object.keys( params ) ) {
			const value = params[ param ];

			if ( value || ( param === 'min_amount' && minAmountStr ) ) { // allow min_amount with a value of 0
				if ( Array.isArray( value ) ) {
					value.forEach( val => urlSearchParams.append( `${param}[]`, val ) );
				} else {
					urlSearchParams.set( param, value );
				}
			}
		}

		if ( urlSearchParams.get( 'page' ) && Number( urlSearchParams.get( 'page' ) ) < 2 ) {
			urlSearchParams.delete( 'page' );
		}

		if ( 'desc' === urlSearchParams.get( 'order' ) ) {
			urlSearchParams.delete( 'order' );
		}

		if ( 'date' === urlSearchParams.get( 'orderby' ) ) {
			urlSearchParams.delete( 'orderby' );
		}

		// sort to get more cache hits
		urlSearchParams.sort();

		return urlSearchParams;
	}

	useEffect( () => {
		if ( ! ready ) {
			// we don't want this to run on first render
			setReady( true );
		} else if ( ! isPopstate ) {
			// load new data from the server is not coming from history cache
			setLoading( true );

			const requestParams = getUrlSearchParams();
			const queryString   = requestParams.toString();

			fetch( `${ api }/grants?${ queryString }` ).then( resp => {
				return resp.json();
			}).then( body => {
				setResponse( body );

				if ( canPushState ) {
					history.pushState(
						{
							params,
							response: body,
							minAmountStr,
							maxAmountStr,
						},
						'',
						`${ location.origin }${ location.pathname }${ queryString ? '?' : '' }${ queryString }`
					);
				}
			}).catch(() => {
				console.error( error );
				setError( error );
			}).finally(() => {
				// reset push/pop/loading values when done
				setLoading( false );
				setCanPushState( true );
				setIsPopstate( false );

				setTimeout(() => {
					if ( scrollRef && scrollRef.current instanceof HTMLElement && scrollRef.current.getBoundingClientRect().top < 0 ) {
						scrollRef.current.scrollIntoView( { behavior: 'smooth' } );
					}
				}, 50 );
			});
		} else {
			// successful push/pop state - reset
			setCanPushState( true );
			setIsPopstate( false );
		}
	}, [ params ] );

	useEffect( () => {
		// reload the previous data on back
		const onPopstate = (e) => {
			setCanPushState( false );

			if ( e.state && e.state.params && e.state.response ) {
				setIsPopstate( true );
				// use popped state if possible
				setParams( e.state.params );
				setResponse( e.state.response );
				setMinAmountStr( e.state.minAmountStr || '' );
				setMaxAmountStr( e.state.maxAmountStr || '' );
			} else {
				// fallback to reloading the url params
				setParams( parseUrlSearchParams() );
			}
		}

		window.addEventListener( 'popstate', onPopstate );
		return () => window.removeEventListener('popstate', onPopstate );
	}, [] );

	useEffect( () => {
		// update the active filter count whenever any facets change
		let c = 0;

		if ( facets ) {
			c += Object.values( facets ).reduce((sum, facet) => {
				sum += facet.filters.filter( filter => filter.active ).length;
				return sum;
			}, 0);
		}

		if ( search ) {
			c++;
		}

		if ( ( minAmountStr || maxAmountStr ) && typeof params.min_amount !== 'undefined' && params.max_amount !== 'undefined' && amountRange.length > 0 ) {
			c++;
		}

		if ( yearFilter ) {
			c++;
		}

		setFilterCount( c );
	}, [ facets, search, min_amount, max_amount, amountRange ] );

	function formatMinValue( value ) {
		if ( value ) {
			const min = parseNumericStr( value );

			if ( ! isNaN( min ) ) {
				setMinAmountStr( USDollar.format( Math.max( min, 0 ) ) );
			}
		}
	}

	function formatMaxValue( value ) {
		if ( value ) {
			const max = parseNumericStr( value );

			if ( ! isNaN( max ) ) {
				setMaxAmountStr( USDollar.format( Math.max( max, 0 ) ) );
			}
		}
	}

	if ( error ) {
		return (
			<div className="grants-db__error error">
				<p>{ error.toString() }</p>
				<p><a href="javascript:location.reload()">{ errorReloadLabel }</a></p>
			</div>
		);
	}

	return (
		<form
			className={ classNames({
				'grants-db__form': true,
				'grants-db__form--loading': loading,
				[ `grants-db__form--order-${order}` ]: order,
				[ `grants-db__form--orderby-${orderby}` ]: orderby,
			} ) }
			method="GET"
			onSubmit={ (e) => {
				e.preventDefault();

				const submitParams = {};

				if ( ( minAmountStr || maxAmountStr ) && ! isValidAmountRange ) {
					return;
				}

				formatMinValue( minAmountStr );
				formatMaxValue( maxAmountStr );

				if ( ( minYear || maxYear ) && ! isValidDateRange ) {
					return;
				}

				if ( searchRef.current && searchRef.current.value ) {
					submitParams.search = searchRef.current.value;
				}

				if ( isValidAmountRange ) {
					submitParams.min_amount = minAmountInt;
					submitParams.max_amount = maxAmountInt;
				}

				if ( isValidDateRange ) {
					if ( minYearInt > 0 ) {
						submitParams.after  = minYearInt;
					}

					if ( maxYearInt > 0 ) {
						submitParams.before = maxYearInt;
					}

					setYearFilter( [ minYearInt, maxYearInt ].filter( ( val, i, arr ) => val && arr.indexOf( val ) === i ).join( ' - ' ) );
				}

				mergeParams( submitParams );
			} }
		>
			<div className="grants-db__header" ref={ scrollRef }>
				<div className="grants-db__header-wrap">
					<fieldset className="grants-db__search">
						<legend className="h5">{ legend }</legend>

						<label className="grants-db__search-label">
							<input
								name="search"
								type="text"
								className="grants-db__search-input"
								defaultValue={ search }
								placeholder={ searchPlaceholder }
								ref={ searchRef }
							/>
							<button type="submit">
								<Icon name="search" />
							</button>
						</label>
					</fieldset>
					<div className="grants-db__summary">
						<TotalStatement
							totalGrants={ total_grants }
							totalGrantees={ total_grantees }
							totalAmount={ total_amount }
							minYear={ minYear || 2006 }
							maxYear={ maxYear || new Date().getFullYear() }
						/>
						<div className="grants-db__remark" dangerouslySetInnerHTML={ { __html: remark } } />
					</div>
					{ filterCount > 0 && (
						<div className="grants-db__active">
							<div className="grants-db__active-header">
								<span className="grants-db__active-label">
									{ `${filterCount} ${filterCount === 1 ? 'filter' : 'filters' } applied` }
								</span>
								<button
									type="button"
									className="grants-db__active-clear"
									onClick={ () => {
										setParams( {} );
										setMinAmountStr( '' );
										setMaxAmountStr( '' );
										setMinYear( '' );
										setMaxYear( '' );
										setYearFilter( '' );
									} }
								>
									{ clearLabel } <Icon name="clear" />
								</button>
							</div>

							<div className="grants-db__active-filters wp-block-buttons">
								{ params.search && (
									<RemoveFilterButton
										label={ searchLabel }
										text={ params.search }
										onClick={ () => mergeParams( { search: '' } ) }
									/>
								) }

								{ yearFilter ? (
									<RemoveFilterButton
										label={ approvalDateLabel }
										text={ yearFilter }
										onClick={ () => {
											setMaxYear( '' );
											setMinYear( '' );
											setYearFilter( null );
											mergeParams( { before: '', after: '' } )
										} }
									/>
								) : null }

								{ ( ( minAmountInt || maxAmountInt ) && isValidAmountRange && typeof params.min_amount !== 'undefined' && typeof params.max_amount !== 'undefined' && amountRange.length > 0 ) ? (
									<RemoveFilterButton
										label={ amountLabel }
										text={ amountRange.map( amount => typeof amount === 'number' ? USDollar.format( amount ) : '' ).join( ' - ' ) }
										onClick={ () => {
											setMinAmountStr( '' );
											setMaxAmountStr( '' );

											const { min_amount, max_amount, ...rest } = params;
											setParams( rest );
										} }
									/>
								) : null }

								{ Object.values( facets ).map( facet => {
									return facet.filters.filter( f => f.active ).map( ( filter, i, filters ) => (
										<RemoveFilterButton
											key={ filter.name }
											label={ filterLabelMap[ facet.name ] || facet.label }
											text={ filter.name }
											onClick={ () => mergeParams( { [ facet.name ]: Array.isArray( params[ facet.name ] ) ? params[ facet.name ].filter( slug => slug !== filter.slug ) : '' } ) }
										/>
									) );
								} ) }
							</div>
						</div>
					) }
				</div>
			</div>
			<div className="grants-db__filter-toggle">
				<button
					type="button"
					className="wp-block-button__link"
					onClick={ () => setFiltersOpen( ! filtersOpen ) }
				>
					Filter Results <Icon name={ filtersOpen ? 'clear' : 'menu' } />
				</button>
			</div>
			<div className="grants-db__content">
				<div className={ classNames( 'grants-db__aside', { 'grants-db__aside--open': filtersOpen } ) }>
					<h2 className="h6">{ asideLabel }</h2>

					<div className="grants-db__facets">
						{ facets.grant_subject && (
							<Facet
								label={ grantSubjectLabel }
								initOpen={ facets.grant_subject.active }
								count={ facets.grant_subject.active }
							>
								<FilterList
									value={ params.grant_subject ?? [] }
									options={ facets.grant_subject.filters }
									onChange={ grant_subject => mergeParams( { grant_subject } ) }
								/>
							</Facet>
						) }
						<Facet
							label={ approvalDateLabel }
							initOpen={ facets?.grant_location?.filters.some( grant_location => grant_location.active ) }
							count={ facets?.grant_location?.active }
						>
							<label>
								{ minApprovalDateLabel }
								<input
									type="text"
									name="after"
									placeholder={ minApprovalDatePlaceholder }
									value={ minYear }
									onChange={ e => setMinYear( e.target.value ) }
								/>
							</label>
							<label>
								{ maxApprovalDateLabel }
								<input
									type="text"
									name="before"
									placeholder={ maxApprovalDatePlaceholder }
									value={ maxYear }
									onChange={ e => setMaxYear( e.target.value ) }
								/>
							</label>

							{ ( ! isValidDateRange ) && (
								<p className="error grants-db__error"><em>{ invalidDateError }</em></p>
							) }

							<div className="wp-block-button is-style-btn-small">
								<button
									type="submit"
									className="wp-block-button__link"
									disabled={ ! isValidDateRange }
								>
									{ applyLabel }
								</button>
							</div>
						</Facet>
						{ facets.program_tax && (
							<Facet
								label={ programsLabel }
								initOpen={ facets.program_tax.active }
								count={ facets.program_tax.active }
							>
								<FilterList
									value={ params.program_tax ?? [] }
									options={ facets.program_tax.filters }
									onChange={ program_tax => mergeParams( { program_tax } ) }
								/>
							</Facet>
						) }
						{ facets.regions_tax && (
							<Facet
								label={ regionLabel }
								initOpen={ facets.regions_tax.active }
								count={ facets.regions_tax.active }
							>
								<FilterList
									value={ params.regions_tax ?? [] }
									options={ facets.regions_tax.filters }
									onChange={ regions_tax => mergeParams( { regions_tax } ) }
								/>
							</Facet>
						) }
						<Facet
							label={ granteeLabel }
							initOpen={ facets?.grantee?.filters?.some( grantee => grantee.active ) }
							count={ facets?.grantee?.active }
						>
							<TermSearch
								label={ granteeSearchLabel }
								taxonomy="grantee"
								value={ params.grantee ?? [] }
								results={ facets?.grantee?.filters ?? [] }
								onChange={ grantee => mergeParams( { grantee } ) }
								api={ api }
							/>
						</Facet>
						<Facet
							label={ grantLocationLabel }
							initOpen={ facets?.grant_location?.filters.some( grant_location => grant_location.active ) }
							count={ facets?.grant_location?.active }
						>
							<TermSearch
								label={ grantLocationSearchLabel }
								taxonomy="grant_location"
								value={ params.grant_location }
								results={ facets?.grant_location?.filters ?? [] }
								onChange={ grant_location => mergeParams( { grant_location } ) }
								api={ api }
							/>
						</Facet>
						<Facet
							label={ amountLabel }
							initOpen={ min_amount && max_amount && isValidAmountRange }
							count={ facets?.grant_location?.active }
						>
							<label>
								{ minAmountLabel }
								<input
									type="text"
									name="amount_min"
									placeholder={ minAmountPlaceholder }
									value={ minAmountStr }
									onChange={ e => setMinAmountStr( e.target.value ) }
									onBlur={ e => formatMinValue( e.target.value ) }
								/>
							</label>
							<label>
								{ maxAmountLabel }
								<input
									type="text"
									name="amount_max"
									placeholder={ maxAmountPlaceholder }
									value={ maxAmountStr }
									onChange={ e => setMaxAmountStr( e.target.value ) }
									onBlur={ e => formatMaxValue( e.target.value ) }
								/>
							</label>

							{ ! isValidAmountRange && ( minAmountStr || maxAmountStr ) && (
								<p className="error grants-db__error"><em>{ invalidAmountError }</em></p>
							) }

							<div className="wp-block-button is-style-btn-small">
								<button
									type="submit"
									className="wp-block-button__link"
									disabled={ ! isValidAmountRange }
								>
									{ applyLabel }
								</button>
							</div>
						</Facet>
						{ facets.grant_type && (
							<Facet
								label={ grantTypeLabel }
								initOpen={ facets.grant_type.active }
							>
								<FilterList
									value={ params.grant_type }
									options={ facets.grant_type.filters }
									onChange={ grant_type => mergeParams( { grant_type } ) }
								/>
							</Facet>
						) }
					</div>
				</div>
				<div className="grants-db__data">
					<label className="grants-db__sort-select">
						<span>Sort By</span>
						<select
							value={ `${orderby}-${order}` }
							onChange={ (e) => {
								const [ optOrderby, optOrder ] = e.target.value.split('-');

								mergeParams( {
									orderby: optOrderby,
									order  : optOrder
								} );
							} }
						>
							<option value="date-desc">Newest</option>
							<option value="date-asc">Oldest</option>
							<option value="amount-asc">Lowest Amount</option>
							<option value="amount-desc">Highest Amount</option>
							<option value="grantee-asc">Result (A-Z)</option>
							<option value="grantee-desc">Result (Z-A)</option>
						</select>
					</label>

					<div className="grants-db__results">
						{ grants && grants.length > 0 ? (
							<>
								<nav className="grants-db__results-sorts">
									<div className="grants-db__results-sort">
										<SortLink
											orderby="amount"
											order={ 'amount' === orderby && 'desc' === order ? 'asc' : 'desc' }
											active={ 'amount' === orderby }
											params={ getUrlSearchParams() }
											onClick={ () => {
												mergeParams( {
													order  : 'amount' === orderby && 'desc' === order ? 'asc': 'desc',
													orderby: 'amount',
													page   : 1,
												} );
											} }
										>
											{ amountTh }
										</SortLink>
									</div>
									<div className="grants-db__results-sort">
										<SortLink
											orderby="grantee"
											order={ 'grantee' === orderby && 'desc' === order ? 'asc' : 'desc' }
											active={ 'grantee' === orderby }
											params={ getUrlSearchParams() }
											onClick={ () => {
												mergeParams( {
													order  : 'grantee' === orderby && 'desc' === order ? 'asc': 'desc',
													orderby: 'grantee',
													page   : 1,
												} );
											} }
										>
											{ resultsTh }
										</SortLink>
									</div>
									<div className="grants-db__results-sort">
										<SortLink
											orderby="date"
											order={ 'date' === orderby && 'desc' === order ? 'asc' : 'desc' }
											active={ 'date' === orderby }
											params={ getUrlSearchParams() }
											onClick={ () => {
												mergeParams( {
													order  : 'date' === orderby && 'desc' === order ? 'asc': 'desc',
													orderby: 'date',
													page   : 1,
												} );
											} }
										>
											{ approvalDateTh }
										</SortLink>
									</div>
								</nav>
								{ grants.map( grant =>
									<Grant
										key={ grant.grant_key }
										{...grant}
									/>
								) }
							</>
						) : (
							<p className="has-text-align-center">{ emptyMessage }</p>
						) }
					</div>
					{ grants && grants.length > 0 && (
						<Pagination
							page={ params.page || 1 }
							max={ total_pages }
							onChange={ page => mergeParams( { page } ) }
						/>
					) }
				</div>
			</div>
		</form>
	);
}

export default function grantsDb( selector = '.grants-db', props ) {
	return Array.from( document.querySelectorAll( selector ) ).forEach( node => {
		const dataNode = node.querySelector('script[type="application/json"]');

		if ( dataNode ) {
			try {
				const data = JSON.parse( dataNode.innerHTML );

				props = { ...data, ...props };
			} catch ( fault ) {
				console.error( fault.toString() );
				return fault;
			}
		}

		const root = createRoot( node );
		root.render( <GrantsDb {...props} /> );

		return root;
	} );
}

document.addEventListener('DOMContentLoaded', () => grantsDb());
